import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { User } from "../../models/auth.models";
import { environment } from "src/environments/environment";
import { LoginResponseDto } from "./dto/auth.dto";
import { jwtDecode } from "jwt-decode"; // Import jwt-decode library

@Injectable({ providedIn: "root" })
export class AuthService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  private apirUrl = environment.apiUrl;

  constructor(private http: HttpClient) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem("currentUser")!),
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  /**
   * current user
   */
  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  /**
   * Get the stored permissions from the JWT
   */
  public getUserPermissions(): any {
    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      return JSON.parse(storedPermissions);
    }

    // Se não existir no localStorage, tenta pegar do token
    const currentUser = this.currentUserSubject.value;
    if (currentUser && currentUser.token) {
      try {
        const decodedToken: any = jwtDecode(currentUser.token);
        const permissoes = decodedToken.permissions
          ? decodedToken.permissions
          : [];

        return permissoes;
      } catch (error) {
        console.error('Erro ao decodificar token JWT:', error);
      }
    }
    return [];
  }

  /**
   * Performs the auth
   * @param email email of user
   * @param password password of user
   */
  login(email: string, password: string) {
    return this.http
      .post<LoginResponseDto>(
        `${this.apirUrl}/v1/auth/login`,
        {
          email,
          senha: password,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )
      .pipe(
        map((data) => {
          if (data && data.accessToken && data.refreshToken) {
            // Decode the access token to get user details
            const decodedToken: any = jwtDecode(data.accessToken);

            // Store access token and refresh token in localStorage
            localStorage.setItem("accessToken", data.accessToken);
            localStorage.setItem("refreshToken", data.refreshToken);
            localStorage.setItem(
              "currentUser",
              JSON.stringify({ ...decodedToken, token: data.accessToken }),
            );

            localStorage.setItem(
              "userPermissions",
              JSON.stringify(decodedToken.permissions),
            );

            // Update the BehaviorSubject with the new user details
            this.currentUserSubject.next({
              ...decodedToken,
              token: data.accessToken,
            });

            // Return the user object
            return decodedToken;
          }

          // Return null if the login response is invalid
          return null;
        }),
      );
  }

  /**
   * Realiza solicitação de reset de senha. codigo é enviado via email
   * @param email
   * @returns 
   */
  solicitarResetPassword(email: string) {

    let paylaod = {
      email: email
    }

    return this.http.post(`${this.apirUrl}/v1/auth/solicitar-redefinir-senha`, email,
      {
        headers: {
          "Content-Type": "application/json",
        },
      })
  }

  resetPassword(email: string, senha: string, confirmarSenha: string, codigo: string) {
    let payload = {
      email: email,
      senha: senha,
      confirmarSenha: confirmarSenha,
      codigo: codigo
    }
    return this.http.post(`${this.apirUrl}/v1/auth/redefinir-senha`, payload,
      {
        headers: {
          "Content-Type": "application/json",
        },
      })

  }

  /**
   * Logout the user
   */
  logout() {
    // remove user from local storage to log user out
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("userPermissions");
    this.currentUserSubject.next(null!);
  }

  /**
   * Verifica se o usuário tem uma permissão específica.
   * @param nomeRole Nome da role a ser verificada.
   * @param acao Ação a ser verificada (ex: 'Editar', 'Ver').
   * @returns boolean Retorna true se a permissão estiver ativa.
   */
  verificarPermissao(nomeRole: string, acao: string): boolean {

    let permissoes = this.getUserPermissions();

    // Se getUserPermissions retornar uma string, faz o parse
    if (typeof permissoes === 'string') {
      try {
        permissoes = JSON.parse(permissoes);
      } catch (error) {
        console.error('Erro ao parsear JSON de permissões:', error);
        return false;
      }
    }

    if (permissoes && permissoes.Root) {
      return permissoes.Root.some((produto: any) =>
        produto.Permissoes.some((grupo: any) =>
          grupo.Roles.some((role: any) =>
            role.NomeRole === nomeRole && role.Acao[acao]
          )
        )
      );
    }
    return false;
  }

}
